// Préparer un chantier (conception § 2.4 à 2.7) : tout est modifiable avant le lancement — postes (renommer, lieu, personnes
// minimum, rattacher à un bâtiment / étage / zone ou à un équipement, scinder, fusionner, réordonner, supprimer), étapes
// (ajouter « hors devis », corriger quantité et heures, déplacer, supprimer), hypothèses, effectif, responsable, dates.
// Après le lancement, la même page sert à faire évoluer la structure : on ajoute, on corrige (tracé), on RETIRE — rien ne s'efface.
// Téléphone d'abord : l'action principale (« Lancer le chantier ») reste collée en bas de l'écran, au-dessus des 5 onglets.
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowDown, ArrowUp, Camera, Flag, FlaskConical, MapPin, Pencil, Plus, Rocket, Scissors, Combine, Trash2, Undo2, Wrench,
} from 'lucide-react';
import { api, ErreurApi, messageErreur } from '../../../lib/api.ts';
import { date, nombre, pluriel } from '../../../lib/format.ts';
import {
  Alerte, Badge, Bouton, Champ, ChampChoix, ChampTexte, Chargement, Dialogue, EntetePage, EtatErreur, FilContexte, LienBouton, Panneau,
  useConfirmation, useNotifier,
} from '../../../components/ui.tsx';
import { BadgeStatutChantier } from '../../../components/statuts.tsx';
import { BandeauRevision, heures, quantite, ResumeEffectif, ResumeTemps, TempsEtape } from './commun.tsx';
import type { Etape, Poste, VueChantier } from './types.ts';
import { DialogueRepartition } from './DialogueRepartition.tsx';

type Muter = (fn: (version: number) => Promise<unknown>, succes?: string) => Promise<boolean>;

/** Écritures : version attendue envoyée, version suivante retenue ; conflit = rechargement et message clair. */
function useMutations(id: string, v: VueChantier | undefined) {
  const qc = useQueryClient();
  const notifier = useNotifier();
  const version = useRef<number>(0);
  useEffect(() => { if (v) version.current = v.chantier.version; }, [v?.chantier.version]);
  const muter: Muter = async (fn, succes) => {
    try {
      const r = await fn(version.current) as { version?: number } | undefined;
      if (r?.version) version.current = r.version;
      if (succes) notifier(succes);
      await qc.invalidateQueries({ queryKey: ['chantier', id] });
      return true;
    } catch (e) {
      notifier(messageErreur(e), true);
      if (e instanceof ErreurApi && e.code === 'version_perimee') await qc.invalidateQueries({ queryKey: ['chantier', id] });
      return false;
    }
  };
  return muter;
}

const vide = (s: string | null | undefined) => (s === null || s === undefined ? '' : s);
const affNombre = (s: string | null | undefined) => (s === null || s === undefined ? '' : nombre(s, 4).replace(/ | /g, ''));

// ─── Hypothèses et tableau « combien de personnes, combien de jours » ───
function PanneauEffectif({ v, muter, id }: { v: VueChantier; muter: Muter; id: string }) {
  const h = v.hypotheses;
  const [hj, setHj] = useState(affNombre(h.heures_sur_site_par_jour.valeur));
  const [rd, setRd] = useState(affNombre(h.rendement_pct.valeur));
  const [jours, setJours] = useState(h.choix?.mode === 'jours' ? String(h.choix.jours) : '');
  useEffect(() => { setHj(affNombre(h.heures_sur_site_par_jour.valeur)); setRd(affNombre(h.rendement_pct.valeur)); }, [h.heures_sur_site_par_jour.valeur, h.rendement_pct.valeur]);
  const e = v.effectif;
  const source = (s: string) => (s === 'chantier' ? 'Propre à ce chantier' : s === 'entreprise' ? 'Valeur de l’entreprise' : s === 'defaut' ? '100 % = aucune correction' : 'Non saisie');
  const patch = (corps: object, msg?: string) => muter((version) => api.patch(`/api/chantiers/${id}`, { version, ...corps }), msg);
  const enregistrerHj = () => {
    const nouv = hj.trim();
    if (nouv === affNombre(h.heures_sur_site_par_jour.valeur)) return;
    void patch({ heuresSurSiteParJour: nouv || null });
  };
  const enregistrerRd = () => {
    const nouv = rd.trim();
    if (nouv === affNombre(h.rendement_pct.valeur)) return;
    void patch({ rendementPct: nouv || null });
  };
  const retenu = e.disponible ? e.retenu : null;
  return (
    <Panneau titre="Combien de personnes, combien de jours">
      <div className="pile">
        <div className="grille-form">
          <Champ libelle="Heures de travail sur site, par jour et par personne" inputMode="decimal" value={hj} onChange={(x) => setHj(x.target.value)} onBlur={enregistrerHj}
            aide={h.heures_sur_site_par_jour.source === 'absente' ? 'À saisir : journée moins trajets et pauses (ou une fois pour toutes dans Administration › Chantiers).' : source(h.heures_sur_site_par_jour.source)} />
          <Champ libelle="Rendement sur ce chantier (%)" inputMode="decimal" value={rd} onChange={(x) => setRd(x.target.value)} onBlur={enregistrerRd}
            aide={`${source(h.rendement_pct.source)} — part de la journée réellement productive (accès difficile, site occupé…).`} />
        </div>
        {(h.heures_sur_site_par_jour.source === 'chantier' || h.rendement_pct.source === 'chantier') && (
          <div><Bouton icone={<Undo2 aria-hidden />} onClick={() => patch({ heuresSurSiteParJour: null, rendementPct: null }, 'Hypothèses de l’entreprise reprises.')}>Reprendre les valeurs de l’entreprise</Bouton></div>
        )}
        {!e.disponible ? <Alerte ton="info">{e.raison}</Alerte> : (
          <>
            <p className="petit secondaire" style={{ margin: 0 }}>
              Capacité d’une personne : {nombre(e.capacite_jour, 2)} h par jour — {nombre(e.jours_personne, 2)} jours-personne pour {heures(v.temps.site)} sur site.
              {' '}Jours de travail arrondis au jour supérieur ; ni jours fériés ni congés (les dates se posent dans le planning).
              {e.base_incomplete && ' Calcul sur les heures connues seulement.'}
            </p>
            <fieldset className="ch-tableau-effectif">
              <legend className="champ-libelle">Effectif — choisissez une ligne</legend>
              {e.lignes.map((l) => (
                <label key={l.personnes} className={`ch-choix ${retenu?.mode === 'effectif' && retenu.personnes === l.personnes ? 'est-choisi' : ''}`}>
                  <input type="radio" name="effectif" checked={retenu?.mode === 'effectif' && retenu.personnes === l.personnes}
                    onChange={() => patch({ choix: { mode: 'effectif', personnes: l.personnes } })} />
                  <span className="chiffre"><strong>{pluriel(l.personnes, 'personne')}</strong> → {pluriel(l.jours, 'jour')}</span>
                </label>
              ))}
            </fieldset>
            <p className="petit discret" style={{ margin: 0 }}>
              Au plus {pluriel(e.max_utile, 'personne')} en même temps (personnes minimum des postes sur site qui ont du travail{e.max_utile_non_precise ? ' ; « non précisé » compté 1' : ''}).
            </p>
            <div className="ch-jours">
              <Champ libelle="Ou imposer un nombre de jours de travail" inputMode="numeric" value={jours} onChange={(x) => setJours(x.target.value.replace(/\D/g, '').slice(0, 4))} />
              <Bouton disabled={!jours || Number(jours) < 1} onClick={() => patch({ choix: { mode: 'jours', jours: Number(jours) } })}>Calculer l’effectif</Bouton>
            </div>
            <ResumeEffectif e={e} />
          </>
        )}
      </div>
    </Panneau>
  );
}

// ─── En-tête du chantier : intitulé, responsable, dates, consignes d'accès ───
function PanneauChantier({ v, muter, id }: { v: VueChantier; muter: Muter; id: string }) {
  const c = v.chantier;
  const personnes = useQuery({ queryKey: ['chantier-personnes', id], queryFn: () => api.get<{ id: string; nom: string }[]>(`/api/chantiers/${id}/personnes`) });
  const [titre, setTitre] = useState(c.title);
  const [consignes, setConsignes] = useState(vide(c.site_notes));
  useEffect(() => { setTitre(c.title); setConsignes(vide(c.site_notes)); }, [c.title, c.site_notes]);
  const patch = (corps: object) => muter((version) => api.patch(`/api/chantiers/${id}`, { version, ...corps }));
  return (
    <Panneau titre="Chantier">
      <div className="grille-form">
        <Champ libelle="Intitulé" value={titre} maxLength={300} onChange={(e) => setTitre(e.target.value)}
          onBlur={() => { if (titre.trim() && titre.trim() !== c.title) void patch({ titre: titre.trim() }); }} />
        <ChampChoix libelle="Responsable du chantier" value={c.responsable?.id ?? ''} vide="Non désigné"
          onChange={(e) => patch({ responsableId: e.target.value || null })}
          options={(personnes.data ?? []).map((p) => ({ valeur: p.id, libelle: p.nom }))}
          aide={personnes.isError ? messageErreur(personnes.error) : 'Comptes actifs ayant accès à ce site.'} />
        <Champ libelle="Début prévu" type="date" value={vide(c.planned_start)} onChange={(e) => patch({ debutPrevu: e.target.value || null })} />
        <Champ libelle="Fin prévue" type="date" value={vide(c.planned_end)} min={c.planned_start ?? undefined} onChange={(e) => patch({ finPrevue: e.target.value || null })} />
      </div>
      <div style={{ marginTop: 'var(--e-4)' }}>
        <ChampTexte libelle="Consignes d’accès" rows={2} maxLength={2000} value={consignes} onChange={(e) => setConsignes(e.target.value)}
          onBlur={() => { if (consignes.trim() !== vide(c.site_notes)) void patch({ consignesAcces: consignes.trim() || null }); }}
          aide="Badge, clés, horaires, local technique… (visibles des techniciens affectés)." />
      </div>
    </Panneau>
  );
}

// ─── Dialogues ───
interface Emplacements { lieux: { id: string; name: string; kind: string }[]; equipements: { id: string; label: string; reference: string | null }[] }
const TYPES_LIEU: Record<string, string> = { batiment: 'Bâtiment', etage: 'Étage', zone: 'Zone' };

function DialoguePoste({ id, poste, fermer, muter }: { id: string; poste: Poste | null; fermer: () => void; muter: Muter }) {
  const emp = useQuery({ queryKey: ['chantier-emplacements', id], queryFn: () => api.get<Emplacements>(`/api/chantiers/${id}/emplacements`) });
  const [label, setLabel] = useState(poste?.label ?? '');
  const [lieu, setLieu] = useState<'site' | 'bureau'>(poste?.place ?? 'site');
  const [crew, setCrew] = useState(poste?.crew_min ? String(poste.crew_min) : '');
  const [loc, setLoc] = useState(poste?.location?.id ?? '');
  const [eq, setEq] = useState(poste?.equipment?.id ?? '');
  const [erreur, setErreur] = useState<string | null>(null);
  const valider = async () => {
    if (!label.trim()) { setErreur('Donnez un libellé au poste.'); return; }
    const corps = { label: label.trim(), lieu, personnesMin: crew ? Number(crew) : null, locationId: loc || null, equipmentId: eq || null };
    const ok = await muter((version) => poste ? api.patch(`/api/chantiers/${id}/postes/${poste.id}`, { version, ...corps })
      : api.post(`/api/chantiers/${id}/postes`, { version, ...corps }), poste ? 'Poste modifié.' : 'Poste ajouté.');
    if (ok) fermer();
  };
  return (
    <Dialogue titre={poste ? 'Modifier le poste' : 'Ajouter un poste'} ouvert fermer={fermer}
      pied={<><Bouton onClick={fermer}>Annuler</Bouton><Bouton variante="principal" onClick={valider}>Enregistrer</Bouton></>}>
      <div className="pile">
        {erreur && <Alerte ton="danger">{erreur}</Alerte>}
        <Champ libelle="Libellé du poste" value={label} maxLength={300} onChange={(e) => setLabel(e.target.value)} placeholder="Ex. : Splits du R+1" />
        <ChampChoix libelle="Lieu du travail" value={lieu} onChange={(e) => setLieu(e.target.value as 'site' | 'bureau')}
          options={[{ valeur: 'site', libelle: 'Sur site (compte dans l’effectif)' }, { valeur: 'bureau', libelle: 'Au bureau (programmation, dossier…)' }]} />
        <ChampChoix libelle="Personnes minimum" value={crew} onChange={(e) => setCrew(e.target.value)} vide="Non précisé (compté 1)"
          options={Array.from({ length: 10 }, (_, i) => ({ valeur: String(i + 1), libelle: pluriel(i + 1, 'personne') }))}
          aide="Postes qui se font obligatoirement à deux : levage d’un groupe, manutention lourde…" />
        <ChampChoix libelle="Bâtiment, étage ou zone" value={loc} onChange={(e) => setLoc(e.target.value)} vide="Aucun"
          options={(emp.data?.lieux ?? []).map((l) => ({ valeur: l.id, libelle: `${TYPES_LIEU[l.kind] ?? l.kind} — ${l.name}` }))} />
        <ChampChoix libelle="Équipement du site" value={eq} onChange={(e) => setEq(e.target.value)} vide="Aucun"
          options={(emp.data?.equipements ?? []).map((x) => ({ valeur: x.id, libelle: `${x.label}${x.reference ? ` (${x.reference})` : ''}` }))}
          aide="Un équipement relié à un chantier garde son historique : il s’archive, il ne s’efface plus." />
      </div>
    </Dialogue>
  );
}

function DialogueEtape({ id, v, posteId, etape, fermer, muter }: { id: string; v: VueChantier; posteId: string; etape: Etape | null; fermer: () => void; muter: Muter }) {
  const lance = v.chantier.status !== 'preparation';
  const [label, setLabel] = useState(etape?.label ?? '');
  const [qty, setQty] = useState(etape ? affNombre(etape.qty_planned) : '1');
  const [unite, setUnite] = useState(etape?.unit ?? 'u');
  const [hpu, setHpu] = useState(etape ? affNombre(etape.hours_per_unit) : '');
  const [detail, setDetail] = useState(vide(etape?.detail));
  const [photo, setPhoto] = useState(etape?.photo_required ?? false);
  const [poste, setPoste] = useState(posteId);
  const [motif, setMotif] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const valider = async () => {
    if (!label.trim()) { setErreur('Donnez un libellé à l’étape.'); return; }
    const corps = { label: label.trim(), quantite: qty, unite: unite.trim() || 'u', heuresParUnite: hpu.trim() || null, detail: detail.trim() || null,
      photoDemandee: photo, posteId: poste, ...(etape && lance && motif.trim() ? { motif: motif.trim() } : {}) };
    const ok = await muter((version) => etape ? api.patch(`/api/chantiers/${id}/etapes/${etape.id}`, { version, ...corps })
      : api.post(`/api/chantiers/${id}/etapes`, { version, ...corps }), etape ? 'Étape modifiée.' : 'Étape ajoutée (hors devis).');
    if (ok) fermer();
  };
  return (
    <Dialogue titre={etape ? 'Modifier l’étape' : 'Ajouter une étape hors devis'} ouvert fermer={fermer}
      pied={<><Bouton onClick={fermer}>Annuler</Bouton><Bouton variante="principal" onClick={valider}>Enregistrer</Bouton></>}>
      <div className="pile">
        {erreur && <Alerte ton="danger">{erreur}</Alerte>}
        {etape?.origin === 'devis' && <p className="petit discret" style={{ margin: 0 }}>Étape issue du devis{etape.reference ? ` (référence ${etape.reference})` : ''} : le lien avec la ligne du devis est conservé.</p>}
        <Champ libelle="Libellé" value={label} maxLength={500} onChange={(e) => setLabel(e.target.value)} />
        <div className="grille-3 ch-grille-etape">
          <Champ libelle="Quantité" inputMode="decimal" value={qty} onChange={(e) => setQty(e.target.value)} />
          <Champ libelle="Unité" value={unite} maxLength={20} onChange={(e) => setUnite(e.target.value)} />
          <Champ libelle="Heures par unité" inputMode="decimal" value={hpu} onChange={(e) => setHpu(e.target.value)} aide="Vide = à renseigner" />
        </div>
        <ChampChoix libelle="Poste" value={poste} onChange={(e) => setPoste(e.target.value)}
          options={v.postes.filter((p) => !p.retire).map((p) => ({ valeur: p.id, libelle: p.label }))} />
        <ChampTexte libelle="Comment faire (facultatif)" rows={2} maxLength={2000} value={detail} onChange={(e) => setDetail(e.target.value)} />
        <label className="case"><input type="checkbox" checked={photo} onChange={(e) => setPhoto(e.target.checked)} /><span>Demander une photo à la coche (ex. raccordement de bus)</span></label>
        {etape && lance && <Champ libelle="Motif de la correction (facultatif, gardé dans l’historique)" value={motif} maxLength={1000} onChange={(e) => setMotif(e.target.value)} />}
      </div>
    </Dialogue>
  );
}

function DialogueScinder({ id, poste, fermer, muter }: { id: string; poste: Poste; fermer: () => void; muter: Muter }) {
  const [label, setLabel] = useState(`${poste.label} (2)`.slice(0, 300));
  const [choix, setChoix] = useState<Set<string>>(new Set());
  const etapes = poste.etapes.filter((e) => !e.retiree);
  const valider = async () => {
    const ok = await muter((version) => api.post(`/api/chantiers/${id}/postes/${poste.id}/scinder`, { version, label: label.trim(), etapes: [...choix] }), 'Poste scindé.');
    if (ok) fermer();
  };
  return (
    <Dialogue titre="Scinder le poste" ouvert fermer={fermer}
      pied={<><Bouton onClick={fermer}>Annuler</Bouton><Bouton variante="principal" disabled={!choix.size || choix.size >= etapes.length || !label.trim()} onClick={valider}>Scinder</Bouton></>}>
      <div className="pile">
        <p className="petit secondaire" style={{ margin: 0 }}>Les étapes cochées partent dans un nouveau poste, placé juste après. Laissez au moins une étape dans « {poste.label} ».</p>
        <Champ libelle="Libellé du nouveau poste" value={label} maxLength={300} onChange={(e) => setLabel(e.target.value)} />
        <fieldset className="ch-cases">
          <legend className="champ-libelle">Étapes à déplacer</legend>
          {etapes.map((e) => (
            <label key={e.id} className="case">
              <input type="checkbox" checked={choix.has(e.id)} onChange={(x) => { const s = new Set(choix); if (x.target.checked) s.add(e.id); else s.delete(e.id); setChoix(s); }} />
              <span>{e.label}</span>
            </label>
          ))}
        </fieldset>
      </div>
    </Dialogue>
  );
}

function DialogueFusion({ id, v, poste, fermer, muter }: { id: string; v: VueChantier; poste: Poste; fermer: () => void; muter: Muter }) {
  const autres = v.postes.filter((p) => p.id !== poste.id && !p.retire);
  const [cible, setCible] = useState(autres[0]?.id ?? '');
  const valider = async () => {
    const ok = await muter((version) => api.post(`/api/chantiers/${id}/postes/${poste.id}/fusionner`, { version, dans: cible }), 'Postes fusionnés.');
    if (ok) fermer();
  };
  return (
    <Dialogue titre="Fusionner le poste" ouvert fermer={fermer}
      pied={<><Bouton onClick={fermer}>Annuler</Bouton><Bouton variante="principal" disabled={!cible} onClick={valider}>Fusionner</Bouton></>}>
      <div className="pile">
        <p className="petit secondaire" style={{ margin: 0 }}>Les étapes et la liste « À poser » de « {poste.label} » rejoignent le poste choisi ; « {poste.label} » disparaît.</p>
        <ChampChoix libelle="Dans le poste" value={cible} onChange={(e) => setCible(e.target.value)} options={autres.map((p) => ({ valeur: p.id, libelle: p.label }))} />
      </div>
    </Dialogue>
  );
}

function DialogueRetrait({ titre, texte, fermer, retirer }: { titre: string; texte: string; fermer: () => void; retirer: (motif: string) => Promise<boolean> }) {
  const [motif, setMotif] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const valider = async () => {
    if (motif.trim().length < 5) { setErreur('Indiquez le motif (5 caractères au moins).'); return; }
    if (await retirer(motif.trim())) fermer();
  };
  return (
    <Dialogue titre={titre} ouvert fermer={fermer}
      pied={<><Bouton onClick={fermer}>Annuler</Bouton><Bouton variante="danger" onClick={valider}>Retirer</Bouton></>}>
      <div className="pile">
        <p className="petit secondaire" style={{ margin: 0 }}>{texte}</p>
        {erreur && <Alerte ton="danger">{erreur}</Alerte>}
        <ChampTexte libelle="Motif" rows={2} maxLength={1000} value={motif} onChange={(e) => setMotif(e.target.value)} />
      </div>
    </Dialogue>
  );
}

function DialogueLancer({ v, fermer, lancer }: { v: VueChantier; fermer: () => void; lancer: () => Promise<void> }) {
  const [envoi, setEnvoi] = useState(false);
  const manque: string[] = [];
  if (!v.chantier.responsable) manque.push('aucun responsable désigné');
  if (!v.chantier.planned_start) manque.push('aucune date de début');
  if (v.temps.incomplet) manque.push(`${pluriel(v.temps.etapes_a_renseigner, 'étape')} sans temps prévu`);
  return (
    <Dialogue titre="Lancer le chantier" ouvert fermer={fermer}
      pied={<><Bouton onClick={fermer}>Retour</Bouton><Bouton variante="principal" icone={<Rocket aria-hidden />} chargement={envoi}
        onClick={async () => { setEnvoi(true); try { await lancer(); } finally { setEnvoi(false); } }}>Lancer le chantier</Bouton></>}>
      <div className="pile">
        <p style={{ margin: 0 }}>Le chantier reçoit son numéro et la structure actuelle devient la <strong>référence</strong> (« prévu au lancement »).
          Ensuite, on pourra encore ajouter une étape ou en retirer une, mais plus rien ne s’efface.</p>
        <p className="petit chiffre" style={{ margin: 0 }}>{pluriel(v.postes.filter((p) => !p.retire).length, 'poste')}, {pluriel(v.temps.nb_etapes, 'étape')}, {heures(v.temps.site)} sur site.</p>
        {manque.length > 0 && <Alerte ton="ambre" titre="À noter avant de lancer">{manque.join(' ; ')}. Le lancement reste possible.</Alerte>}
      </div>
    </Dialogue>
  );
}

// ─── Un poste et ses étapes ───
function CartePoste({ v, p, i, n, id, muter, ouvrir }: {
  v: VueChantier; p: Poste; i: number; n: number; id: string; muter: Muter;
  ouvrir: (d: Ouvert) => void;
}) {
  const lance = v.chantier.status !== 'preparation';
  const { confirmer, element } = useConfirmation();
  const ids = v.postes.map((x) => x.id);
  const deplacerPoste = (sens: -1 | 1) => {
    const o = [...ids]; [o[i], o[i + sens]] = [o[i + sens], o[i]];
    void muter((version) => api.post(`/api/chantiers/${id}/postes/ordre`, { version, ids: o }));
  };
  const deplacerEtape = (j: number, sens: -1 | 1) => {
    const o = p.etapes.map((e) => e.id); [o[j], o[j + sens]] = [o[j + sens], o[j]];
    void muter((version) => api.post(`/api/chantiers/${id}/etapes/ordre`, { version, posteId: p.id, ids: o }));
  };
  const supprimerPoste = async () => {
    if (!(await confirmer('Supprimer le poste ?', `« ${p.label} » et ses ${pluriel(p.etapes.length, 'étape')} seront supprimés de la préparation.`, 'Supprimer le poste'))) return;
    await muter((version) => api.del(`/api/chantiers/${id}/postes/${p.id}?version=${version}`), 'Poste supprimé.');
  };
  const supprimerEtape = async (e: Etape) => {
    if (!(await confirmer('Supprimer l’étape ?', `« ${e.label} » sera supprimée de la préparation.`, 'Supprimer l’étape'))) return;
    await muter((version) => api.del(`/api/chantiers/${id}/etapes/${e.id}?version=${version}`), 'Étape supprimée.');
  };
  return (
    <li className={`ch-poste ${p.retire ? 'ch-retire' : ''}`}>
      <div className="ch-poste-entete">
        <span className="ch-poste-num chiffre" aria-hidden>{i + 1}</span>
        <div className="pile" style={{ gap: 4, minWidth: 0, flex: 1 }}>
          <h3 className="ch-poste-titre">{p.label}</h3>
          <span className="rangee" style={{ gap: 6 }}>
            <span className="petit chiffre">{heures(p.temps.heures)}{p.temps.a_renseigner ? ` + ${p.temps.a_renseigner} à renseigner` : ''} — {pluriel(p.temps.nb_etapes, 'étape')}</span>
            {p.place === 'bureau' && <Badge ton="info" icone={false}>Au bureau</Badge>}
            <Badge icone={false}>{p.crew_min ? `${pluriel(p.crew_min, 'personne')} min.` : 'Personnes : non précisé'}</Badge>
            {p.location && <Badge icone={<MapPin size={14} aria-hidden />}>{p.location.label}</Badge>}
            {p.equipment && <Badge icone={<Wrench size={14} aria-hidden />}>{p.equipment.label}</Badge>}
            {p.ouvrage && <span className="expert-seulement"><Badge icone={false}>Ouvrage <span className="mono">{p.ouvrage.code}</span></Badge></span>}
            {p.retire && <Badge>Retiré : {p.retire.motif}</Badge>}
          </span>
        </div>
      </div>
      <ol className="ch-etapes">
        {p.etapes.map((e, j) => (
          <li key={e.id} className={`ch-etape ${e.retiree ? 'ch-retire' : ''}`}>
            <div className="ch-etape-texte">
              <span className="ch-etape-label">{e.label}{e.photo_required && <Camera size={15} aria-label="Photo demandée" className="discret" style={{ marginLeft: 6, verticalAlign: '-2px' }} />}</span>
              <span className="rangee" style={{ gap: 6 }}>
                <TempsEtape e={e} />
                {e.origin === 'hors_devis' && <Badge icone={false}>Hors devis</Badge>}
                {e.retiree && <span className="petit discret">Retirée : {e.retiree.motif}</span>}
              </span>
            </div>
            {!e.retiree && !p.retire && (
              <div className="ch-etape-actions">
                <Bouton variante="fantome" className="btn-icone" aria-label={`Monter « ${e.label} »`} disabled={j === 0} onClick={() => deplacerEtape(j, -1)}><ArrowUp aria-hidden /></Bouton>
                <Bouton variante="fantome" className="btn-icone" aria-label={`Descendre « ${e.label} »`} disabled={j === p.etapes.length - 1} onClick={() => deplacerEtape(j, 1)}><ArrowDown aria-hidden /></Bouton>
                <Bouton variante="fantome" className="btn-icone" aria-label={`Modifier « ${e.label} »`} onClick={() => ouvrir({ type: 'etape', posteId: p.id, etape: e })}><Pencil aria-hidden /></Bouton>
                {lance
                  ? <Bouton variante="fantome" className="btn-icone" aria-label={`Retirer « ${e.label} »`} onClick={() => ouvrir({ type: 'retrait-etape', etape: e })}><Flag aria-hidden /></Bouton>
                  : <Bouton variante="fantome" className="btn-icone" aria-label={`Supprimer « ${e.label} »`} onClick={() => supprimerEtape(e)}><Trash2 aria-hidden /></Bouton>}
              </div>
            )}
          </li>
        ))}
        {!p.etapes.length && <li className="petit discret ch-etape">Aucune étape.</li>}
      </ol>
      {p.a_poser.length > 0 && (
        <p className="petit ch-a-poser"><strong>À poser :</strong> {p.a_poser.map((a) => `${a.label}${a.reference ? ` (${a.reference})` : ''} × ${quantite(a.qty, a.unit)}`).join(' ; ')}</p>
      )}
      {!p.retire && (
        <div className="ch-poste-actions">
          <Bouton icone={<Plus aria-hidden />} onClick={() => ouvrir({ type: 'etape', posteId: p.id, etape: null })}>Ajouter une étape</Bouton>
          <Bouton icone={<Pencil aria-hidden />} onClick={() => ouvrir({ type: 'poste', poste: p })}>Modifier le poste</Bouton>
          {!lance && v.chantier.devis && <Bouton onClick={() => ouvrir({ type: 'repartir', poste: p })}>Répartir par étage</Bouton>}
          <details className="ch-plus">
            <summary className="btn">Réorganiser…</summary>
            <div className="ch-plus-corps">
              <Bouton icone={<ArrowUp aria-hidden />} disabled={i === 0} onClick={() => deplacerPoste(-1)}>Monter</Bouton>
              <Bouton icone={<ArrowDown aria-hidden />} disabled={i === n - 1} onClick={() => deplacerPoste(1)}>Descendre</Bouton>
              {p.etapes.filter((e) => !e.retiree).length > 1 && <Bouton icone={<Scissors aria-hidden />} onClick={() => ouvrir({ type: 'scinder', poste: p })}>Scinder</Bouton>}
              {!lance && n > 1 && <Bouton icone={<Combine aria-hidden />} onClick={() => ouvrir({ type: 'fusion', poste: p })}>Fusionner dans…</Bouton>}
              {lance
                ? <Bouton variante="danger" icone={<Flag aria-hidden />} onClick={() => ouvrir({ type: 'retrait-poste', poste: p })}>Retirer le poste</Bouton>
                : <Bouton variante="danger" icone={<Trash2 aria-hidden />} onClick={supprimerPoste}>Supprimer le poste</Bouton>}
            </div>
          </details>
        </div>
      )}
      {element}
    </li>
  );
}

type Ouvert =
  | { type: 'poste'; poste: Poste | null } | { type: 'etape'; posteId: string; etape: Etape | null }
  | { type: 'scinder'; poste: Poste } | { type: 'fusion'; poste: Poste }
  | { type: 'repartir'; poste: Poste }
  | { type: 'retrait-poste'; poste: Poste } | { type: 'retrait-etape'; etape: Etape } | { type: 'lancer' };

export function PagePreparer() {
  const { id = '' } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const notifier = useNotifier();
  const q = useQuery({ queryKey: ['chantier', id], queryFn: () => api.get<VueChantier>(`/api/chantiers/${id}`) });
  const muter = useMutations(id, q.data);
  const [ouvert, setOuvert] = useState<Ouvert | null>(null);
  const { confirmer, element } = useConfirmation();
  if (q.isLoading) return <Chargement />;
  if (q.isError) return <EtatErreur erreur={q.error} reessayer={() => q.refetch()} />;
  const v = q.data!;
  const c = v.chantier;
  if (!v.droits.preparer) return <EtatErreur erreur={{ code: 'droit_insuffisant', message: 'La préparation d’un chantier est réservée au chargé d’affaires et à l’administrateur.' }} />;
  const lance = c.status !== 'preparation';
  const fermer = () => setOuvert(null);

  const lancer = async () => {
    try {
      const r = await api.post<{ reference: string }>(`/api/chantiers/${id}/lancer`, { version: c.version });
      notifier(`Chantier ${r.reference} lancé.`);
      await qc.invalidateQueries({ queryKey: ['chantier', id] });
      await qc.invalidateQueries({ queryKey: ['chantiers'] });
      nav(`/interventions/chantiers/${id}`);
    } catch (e) { notifier(messageErreur(e), true); await qc.invalidateQueries({ queryKey: ['chantier', id] }); }
  };
  const abandonner = async () => {
    if (!(await confirmer('Abandonner la préparation ?', 'Postes, étapes et hypothèses de cette préparation seront supprimés. Le devis et son acceptation ne changent pas : vous pourrez préparer de nouveau le chantier.', 'Abandonner la préparation'))) return;
    try {
      await api.del(`/api/chantiers/${id}`);
      notifier('Préparation abandonnée.');
      await qc.invalidateQueries({ queryKey: ['chantiers'] });
      nav(c.devis ? `/etudes/${c.devis.dossier.id}/devis/${c.devis.id}?revision=${c.devis.revision_id}` : '/interventions/chantiers');
    } catch (e) { notifier(messageErreur(e), true); }
  };
  const actifs = v.postes.filter((p) => !p.retire).length;

  let dialogue: ReactNode = null;
  if (ouvert?.type === 'repartir') dialogue = <DialogueRepartition id={id} poste={ouvert.poste} fermer={fermer} muter={muter} />;
  if (ouvert?.type === 'poste') dialogue = <DialoguePoste id={id} poste={ouvert.poste} fermer={fermer} muter={muter} />;
  if (ouvert?.type === 'etape') dialogue = <DialogueEtape id={id} v={v} posteId={ouvert.posteId} etape={ouvert.etape} fermer={fermer} muter={muter} />;
  if (ouvert?.type === 'scinder') dialogue = <DialogueScinder id={id} poste={ouvert.poste} fermer={fermer} muter={muter} />;
  if (ouvert?.type === 'fusion') dialogue = <DialogueFusion id={id} v={v} poste={ouvert.poste} fermer={fermer} muter={muter} />;
  if (ouvert?.type === 'retrait-poste') {
    const p = ouvert.poste;
    dialogue = <DialogueRetrait titre="Retirer le poste" texte={`« ${p.label} » et ses étapes restent visibles, marqués « retirés », avec le motif. Rien n’est effacé.`} fermer={fermer}
      retirer={(motif) => muter((version) => api.post(`/api/chantiers/${id}/postes/${p.id}/retirer`, { version, motif }), 'Poste retiré.')} />;
  }
  if (ouvert?.type === 'retrait-etape') {
    const e = ouvert.etape;
    dialogue = <DialogueRetrait titre="Retirer l’étape" texte={`« ${e.label} » reste visible, marquée « retirée », avec le motif. Rien n’est effacé.`} fermer={fermer}
      retirer={(motif) => muter((version) => api.post(`/api/chantiers/${id}/etapes/${e.id}/retirer`, { version, motif }), 'Étape retirée.')} />;
  }
  if (ouvert?.type === 'lancer') dialogue = <DialogueLancer v={v} fermer={fermer} lancer={lancer} />;

  return (
    <div className="pile-l ch-preparer">
      <EntetePage
        fil={<FilContexte noeuds={[{ type: '', libelle: 'Chantiers', to: '/interventions/chantiers' }, { type: '', libelle: c.reference ?? c.title, to: `/interventions/chantiers/${id}` }, { type: '', libelle: lance ? 'Structure' : 'Préparation' }]} />}
        titre={lance ? 'Faire évoluer la structure' : 'Préparer le chantier'}
        sousTitre={<span className="rangee" style={{ gap: 8 }}><BadgeStatutChantier statut={c.status} />{c.demo && <Badge ton="ambre" icone={<FlaskConical size={14} aria-hidden />}>Démo</Badge>}
          <span className="petit">{c.title}{c.site ? ` — ${c.site.nom}` : ''}{c.devis ? ` — devis ${c.devis.number}, révision ${c.devis.rev_no}${c.devis.acceptee_le ? `, accepté le ${date(c.devis.acceptee_le)}` : ''}` : ' — sans devis'}</span></span>} />
      <BandeauRevision v={v} action={!v.revision_a_reporter && v.revision_plus_recente && c.devis ? <LienBouton to={`/etudes/${c.devis.dossier.id}/devis/${c.devis.id}?revision=${v.revision_plus_recente.revision_id}`}>Voir la révision {v.revision_plus_recente.rev_no}</LienBouton> : undefined} />
      {lance && <Alerte ton="info" titre={`Chantier lancé : la structure de départ est la référence`}>On peut ajouter une étape (marquée « hors devis »), corriger une quantité ou des heures (la correction est gardée dans l’historique de l’étape) ou retirer un poste ou une étape avec un motif. Rien ne s’efface.</Alerte>}

      <Panneau titre="Temps prévu">
        <ResumeTemps v={v} />
      </Panneau>

      <PanneauEffectif v={v} muter={muter} id={id} />
      {(v.avertissements_repartition ?? []).map((a,i)=><Alerte key={i} ton="ambre">{a}</Alerte>)}
      <PanneauChantier v={v} muter={muter} id={id} />

      <Panneau titre={`Postes et étapes — ${pluriel(actifs, 'poste')}`} actions={<Bouton icone={<Plus aria-hidden />} onClick={() => setOuvert({ type: 'poste', poste: null })}>Ajouter un poste</Bouton>}>
        {v.postes.length === 0
          ? <p className="secondaire" style={{ margin: 0 }}>Aucun poste. Ajoutez un poste, puis ses étapes dans l’ordre (avec quantité et heures).</p>
          : <ol className="ch-postes">{v.postes.map((p, i) => <CartePoste key={p.id} v={v} p={p} i={i} n={v.postes.length} id={id} muter={muter} ouvrir={setOuvert} />)}</ol>}
      </Panneau>

      {v.lignes_non_reprises.length > 0 && (
        <details className="panneau ch-non-reprises">
          <summary className="panneau-entete"><strong>{pluriel(v.lignes_non_reprises.length, 'ligne du devis non reprise', 'lignes du devis non reprises')}</strong></summary>
          <ul className="panneau-corps petit">
            {v.lignes_non_reprises.map((l, i) => <li key={i}>{l.label} — {l.motif}{l.heures && l.heures !== '0' ? ` (${heures(l.heures)} au devis)` : ''}</li>)}
          </ul>
        </details>
      )}

      {!lance && (
        <div><Bouton variante="danger" icone={<Trash2 aria-hidden />} onClick={abandonner}>Abandonner la préparation</Bouton></div>
      )}

      <div className="barre-actions barre-collante">
        <span className="petit chiffre barre-collante-resume">{pluriel(actifs, 'poste')} · {pluriel(v.temps.nb_etapes, 'étape')} · {heures(v.temps.site)} sur site{v.temps.incomplet ? ' (incomplet)' : ''}</span>
        {lance
          ? <LienBouton variante="principal" to={`/interventions/chantiers/${id}`}>Voir le chantier</LienBouton>
          : <Bouton variante="principal" icone={<Rocket aria-hidden />} disabled={!v.temps.nb_etapes} onClick={() => setOuvert({ type: 'lancer' })}>Lancer le chantier</Bouton>}
      </div>
      {!lance && !v.temps.nb_etapes && <p className="petit discret" style={{ margin: 0 }}>Ajoutez au moins une étape pour pouvoir lancer le chantier. <Link to="/interventions/chantiers">Retour aux chantiers</Link></p>}
      {dialogue}
      {element}
    </div>
  );
}
